import * as chrono from 'chrono-node';
import type { Priority } from '@shared/types';

/**
 * Parses a quick-capture line into a structured task.
 *
 *   "review Q3 model tomorrow 3pm #close !high"
 *     → { title: "review Q3 model", dueAt: <tomorrow 15:00>, tags: ["close"], priority: 3 }
 *
 * Design rule: anything consumed by a token is removed from the title, and
 * anything NOT understood is left in the title verbatim. Silently swallowing
 * text the user typed is worse than failing to parse it — they would never
 * notice the words had gone.
 */

export interface ParsedCapture {
  title: string;
  dueAt: Date | null;
  tags: string[];
  priority: Priority;
  /** Project name written as `+Budget`, if present. */
  project: string | null;
  /** Human summary of what was understood, for the live preview. */
  matchedText: { date?: string; tags: string[]; priority?: string; project?: string };
}

const PRIORITY_WORDS: Record<string, Priority> = {
  '1': 1,
  '2': 2,
  '3': 3,
  low: 1,
  med: 2,
  medium: 2,
  high: 3,
  urgent: 3,
};

/** `#tag`, `+project`, `!priority` — each must be its own word. */
const TAG_RE = /(?:^|\s)#([\p{L}\p{N}_-]+)/gu;
const PROJECT_RE = /(?:^|\s)\+([\p{L}\p{N}_-]+)/gu;
const PRIORITY_RE = /(?:^|\s)!([\p{L}\p{N}]+)/gu;

/**
 * Stand-in for text that must survive the date parse untouched. A control
 * character is used because chrono will never read one as part of a date, and
 * a user cannot type one into the capture box.
 */
const SENTINEL = '';
const CONTROL_CHARS = /[ -]/g;

export function parseCapture(input: string, now: Date = new Date()): ParsedCapture {
  // Strip control characters up front so pasted text cannot smuggle in a
  // sentinel and corrupt the restore step.
  let working = input.replace(CONTROL_CHARS, ' ');
  const matched: ParsedCapture['matchedText'] = { tags: [] };

  // Tokens are pulled out before the date parse so chrono never sees "#mar" or
  // "+may" and mistakes a tag or project name for a month.
  const tags: string[] = [];
  working = working.replace(TAG_RE, (whole, tag: string) => {
    tags.push(tag.toLowerCase());
    matched.tags.push(`#${tag}`);
    return whole.startsWith(' ') ? ' ' : '';
  });

  let project: string | null = null;
  working = working.replace(PROJECT_RE, (whole, name: string) => {
    project = name;
    matched.project = `+${name}`;
    return whole.startsWith(' ') ? ' ' : '';
  });

  // Text handed back verbatim, hidden from chrono while the date parse runs.
  // Without this, "deploy !now" loses the word "now" to the date parser and
  // leaves behind a stray "!".
  const shielded: string[] = [];

  let priority: Priority = 0;
  working = working.replace(PRIORITY_RE, (whole, word: string) => {
    const lead = whole.startsWith(' ') ? ' ' : '';
    const resolved = PRIORITY_WORDS[word.toLowerCase()];
    // An unrecognised `!word` stays in the title rather than disappearing.
    if (resolved === undefined) {
      shielded.push(`!${word}`);
      return lead + SENTINEL;
    }
    priority = resolved;
    matched.priority = `!${word}`;
    return lead;
  });

  let dueAt: Date | null = null;
  const best = chrono.parse(working, now, { forwardDate: true })[0];

  if (best) {
    dueAt = best.start.date();

    // chrono reports a date for bare times too; if no clock time was stated,
    // treat it as a day-level deadline and pin it to 09:00 rather than
    // inheriting the current time, which would make "tomorrow" mean 12:07am.
    if (!best.start.isCertain('hour')) {
      dueAt.setHours(9, 0, 0, 0);
    }

    matched.date = best.text;
    working = working.slice(0, best.index) + ' ' + working.slice(best.index + best.text.length);
  }

  // Restore only after the date parse has taken its slice, so the indices
  // chrono reported still line up with the string they were measured against.
  // Sentinels are consumed left to right — the order they were pushed.
  const segments = working.split(SENTINEL);
  working = segments.reduce(
    (acc, segment, i) => (i === 0 ? segment : acc + (shielded[i - 1] ?? '') + segment),
    '',
  );

  return {
    title: tidy(working),
    dueAt,
    tags: [...new Set(tags)],
    priority,
    project,
    matchedText: matched,
  };
}

/**
 * Collapses the whitespace left behind by removed tokens and trims dangling
 * connector words, so "review model by" becomes "review model".
 */
function tidy(text: string): string {
  return text
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[\s,]*\b(by|on|at|due|before)\s*$/i, '')
    .trim();
}
